<template>
  <Transition name="greeting-slide" mode="out-in">
    <div v-if="showGreeting" class="greeting-banner" @click="closeGreeting">
      <div class="greeting-content">
        <VaIcon name="waving_hand" color="warning" size="24px" class="greeting-icon" />
        <span class="greeting-text"
          >Bonjour <strong>{{ userLastName }}</strong></span
        >
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { useUserStore } from '../../../stores/user-store'
import { VaIcon } from 'vuestic-ui'

const userStore = useUserStore()
const showGreeting = ref(false)
let greetingTimer: number | null = null
let animationTimer: number | null = null

const userLastName = ref('utilisateur')

// Animation trigger - affiche le greeting au montage du composant
onMounted(() => {
  if (userStore.lastName) {
    userLastName.value = userStore.lastName.toUpperCase()
  }

  // Délai court avant de déclencher l'animation
  animationTimer = window.setTimeout(() => {
    showGreeting.value = true

    // Auto-collapse après 3.5 secondes
    greetingTimer = window.setTimeout(() => {
      showGreeting.value = false
    }, 3500)
  }, 300)
})

// Nettoyage
onBeforeUnmount(() => {
  if (greetingTimer) clearTimeout(greetingTimer)
  if (animationTimer) clearTimeout(animationTimer)
})

// Permet de fermer manuellement le greeting
const closeGreeting = () => {
  showGreeting.value = false
  if (greetingTimer) {
    clearTimeout(greetingTimer)
    greetingTimer = null
  }
}

// Watcher pour réagir aux changements du lastName
watch(
  () => userStore.lastName,
  (newValue) => {
    if (newValue) {
      userLastName.value = newValue.toUpperCase()
    }
  },
)
</script>

<script lang="ts">
import { onBeforeUnmount } from 'vue'
</script>

<style lang="scss" scoped>
.greeting-banner {
  position: fixed;
  top: 70px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  background: linear-gradient(135deg, var(--va-primary) 0%, var(--va-success) 100%);
  border-radius: 8px;
  padding: 12px 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  animation: slideInDown 0.6s ease-out;

  @media screen and (max-width: 640px) {
    width: calc(100% - 40px);
    left: 50%;
    transform: translateX(-50%);
    top: 65px;
  }
}

.greeting-content {
  display: flex;
  align-items: center;
  gap: 10px;
  color: white;
  font-weight: 500;
  font-size: 0.95rem;
}

.greeting-icon {
  animation: wave 0.6s ease-in-out;
}

.greeting-text {
  letter-spacing: 0.5px;

  strong {
    font-weight: 700;
    margin-left: 4px;
  }
}

// Animation de slide-out depuis le haut
@keyframes slideInDown {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

// Animation de wave pour l'icône
@keyframes wave {
  0%,
  100% {
    transform: rotate(0deg);
  }
  25% {
    transform: rotate(-10deg);
  }
  75% {
    transform: rotate(10deg);
  }
}

// Transition Vue pour le greeting
.greeting-slide-enter-active {
  animation: slideInDown 0.6s ease-out;
}

.greeting-slide-leave-active {
  animation: slideOutUp 0.4s ease-in forwards;
}

@keyframes slideOutUp {
  from {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
  to {
    opacity: 0;
    transform: translateX(-50%) translateY(-20px);
  }
}

// Effet de pulse au survol
.greeting-banner:hover {
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  transform: translateX(-50%) translateY(-2px);
  transition: all 0.3s ease;
}
</style>
